import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, output, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DsAutosuggestModule,
  DsButtonModule,
  DsFormFieldModule,
  DsIconModule,
  DsModalModule,
  DsTagModule,
  DsToggleButtonModule,
} from '@bmw-ds/components';
import { TranslateModule } from '@ngx-translate/core';
import { UserStore } from 'src/app/core/stores/user.store';
import { Language } from 'src/app/shared/models/language.model';
import { LANGUAGE_OPTIONS } from '../../../shared/utils/language.utils';

@Component({
  selector: 'pure-language-settings-modal',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DsAutosuggestModule,
    DsTagModule,
    DsIconModule,
    DsButtonModule,
    DsToggleButtonModule,
    DsFormFieldModule,
    TranslateModule,
    DsModalModule,
  ],
  templateUrl: './language-settings-modal.html',
  styleUrl: './language-settings-modal.scss',
})
export class LanguageSettingsModalComponent implements OnInit {
  closed = output<void>();

  private readonly userStore = inject(UserStore);

  modalIsOpen = signal<boolean>(true);
  languageChanged = signal<boolean>(false);

  protected languageOptions: Language[] = [];

  protected reactiveForm = new FormGroup({
    languageToSearch: new FormControl('', {
      validators: [Validators.required],
    }),
  });

  ngOnInit(): void {
    this.populateLanguageOptions();
    this.setUserPreferredLanguage();
  }

  protected populateLanguageOptions(): void {
    this.languageOptions = [];
    this.languageOptions = LANGUAGE_OPTIONS.map((option) => ({
      ...option,
      isActive: false,
    }));
  }

  protected setUserPreferredLanguage(): void {
    let userLanguage = null;
    if (localStorage.getItem('userPreferredLanguage')) {
      userLanguage = this.languageOptions.find(
        (lang) => lang.isoCountryCode === localStorage.getItem('userPreferredLanguage')
      );
    } else {
      const userDefaultLanguage = this.userStore.userInformation()?.userCountryCode.toLowerCase();
      if (userDefaultLanguage) {
        userLanguage = this.languageOptions.find((lang) => lang.isoCountryCode === userDefaultLanguage);
      }
    }
    if (userLanguage) {
      this.onSelectLanguage(userLanguage, false);
    }
  }

  modalDismissed(): void {
    this.modalIsOpen.set(false);
  }

  protected onSelectLanguage(language: Language, languageChanged: boolean): void {
    this.languageOptions.forEach((lang) => (lang.isActive = false));
    language.isActive = true;
    this.languageChanged.set(languageChanged);
  }

  protected searchLanguage(): void {
    const languageFormControl = this.reactiveForm.get('languageToSearch');
    const formControlValue = languageFormControl?.value;

    if (!formControlValue) {
      this.clearSelectedLanguage();
    } else {
      this.languageOptions = this.languageOptions.filter((lang) =>
        lang.name.toLowerCase().includes(formControlValue.toLowerCase())
      );
    }
  }

  protected clearSelectedLanguage(): void {
    const languageFormControl = this.reactiveForm.get('languageToSearch');
    languageFormControl?.setValue('');
    languageFormControl?.setErrors(null);
    this.populateLanguageOptions();
  }

  protected onChangeLanguage(): void {
    const languageToSave = this.languageOptions.find((lang) => lang.isActive)?.isoCountryCode;
    if (languageToSave) {
      localStorage.setItem('userPreferredLanguage', languageToSave);
      this.userStore.setUserPreferredLanguage(languageToSave);
      this.modalDismissed();
    }
  }
}
